import { type Message, type ToolMessage } from '@ag-ui/core';

import { FILE_EDIT_COMPLETE_TOOL_NAME, type FileEditCompleteResult } from '../tools';
import { BRAND_KIT_SETUP_TOOL_NAME, type BrandKitSetupResult } from '../tools/brandKitSetup';
import { ELICIT_MISSING_CONTEXT_TOOL_NAME, type ElicitMissingContextResult } from '../tools/elicitMissingContext';

export type ToolCallStatus = 'working' | 'completed' | 'error';

/** Tool message enriched with the original tool call information */
export type EnrichedToolMessage = ToolMessage & {
  /** The name of the function that was called, or null if not found */
  toolName: string | null;
  /** Localized tool description extracted from tool call arguments, or null */
  toolDescription: string | null;
  /** Whether the tool execution resulted in an error */
  hasError: boolean;
  /** The current status of the tool call */
  status: ToolCallStatus;
  /** Parsed (and for registered tools, schema-validated) result content, or null */
  parsedContent: unknown;
};

/** A group of consecutive tool messages displayed as a single unit */
export type GroupedToolMessages = {
  /** Discriminant for switching in render logic */
  role: 'tool-group';
  /** Stable ID derived from the first message in the group */
  id: string;
  /** The individual enriched tool messages in this group */
  messages: EnrichedToolMessage[];
  /** Aggregate status: working > error > completed */
  status: ToolCallStatus;
};

/** Message types that can be displayed in the chat */
export type DisplayMessage = Exclude<Message, ToolMessage> | EnrichedToolMessage | GroupedToolMessages;

/** Type guard for EnrichedToolMessage */
export const isEnrichedToolMessage = (message: DisplayMessage): message is EnrichedToolMessage => {
  return message.role === 'tool' && 'toolName' in message;
};

/** Type guard for GroupedToolMessages */
export const isGroupedToolMessages = (message: DisplayMessage): message is GroupedToolMessages => {
  return 'role' in message && message.role === 'tool-group';
};

/** Type guard for file_edit_complete tool messages with validated parsedContent */
export const isFileEditCompleteToolMessage = (
  message: EnrichedToolMessage,
): message is EnrichedToolMessage & {
  toolName: typeof FILE_EDIT_COMPLETE_TOOL_NAME;
  parsedContent: FileEditCompleteResult;
} => message.toolName === FILE_EDIT_COMPLETE_TOOL_NAME && message.parsedContent != null;

export const isBrandKitSetupToolMessage = (
  message: EnrichedToolMessage,
): message is EnrichedToolMessage & {
  toolName: typeof BRAND_KIT_SETUP_TOOL_NAME;
  parsedContent: BrandKitSetupResult;
} => message.toolName === BRAND_KIT_SETUP_TOOL_NAME && message.parsedContent != null;

/** Type guard for elicit_missing_context tool messages with validated parsedContent */
export const isElicitMissingContextToolMessage = (
  message: EnrichedToolMessage,
): message is EnrichedToolMessage & {
  toolName: typeof ELICIT_MISSING_CONTEXT_TOOL_NAME;
  parsedContent: ElicitMissingContextResult;
} => message.toolName === ELICIT_MISSING_CONTEXT_TOOL_NAME && message.parsedContent != null;

/** Derive aggregate status from a set of tool messages */
export const getGroupStatus = (messages: EnrichedToolMessage[]): ToolCallStatus => {
  if (messages.some((m) => m.status === 'working')) {
    return 'working';
  }

  if (messages.some((m) => m.status === 'error')) {
    return 'error';
  }

  return 'completed';
};
